import { Deserializer } from "jsonapi-serializer";
import { CREATED, INTERNAL_SERVER_ERROR, NO_CONTENT, OK } from "../constants/response-status-codes";
import { CONTROLLER_COMMANDS, DASHBOARD_PROFILE_ID, LOCATION_COMMANDS, QUICK_ENROLL_BKGCOLOR, READER_COMMANDS, SITE_COMMANDS, TITLE_BKGCOLOR, TITLE_FGCOLOR, UI_ELEMENT_SELECT_MENU_ITEM, WIDGET_DIMENSION, WIDGET_TYPES, X_POSITION, Y_POSITION, ZONE_COMMANDS } from "../constants/widget-constants";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { genericError } from "../models/GenericError";
import { Widget } from "../models/Widget";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { APIConnector } from "./api/APIConnector";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { ListQuery } from "../models/ListQuery";
import { getFilterClause, createPartialURL } from "./utilities/urlBuilder";
import { MENUS } from "../constants/menu-types";

const apiConnector = new APIConnector();

export const storeWidget = async (widget: Widget): Promise<StoreEntityResult> => {
    try {

        const settingData: any = [
            {
                eSysnameLookup: X_POSITION,
                sdValstring: widget.x.toString()
            },
            {
                eSysnameLookup: Y_POSITION,
                sdValstring: widget.y.toString()
            },
            {
                eSysnameLookup: TITLE_BKGCOLOR,
                sdValstring: widget.titleBackgroundColor
            },
            {
                eSysnameLookup: TITLE_FGCOLOR,
                sdValstring: widget.titleForegroundColor
            },
        ];

        if (widget.relationships.length > 0) {
            settingData.push(...widget.relationships)
        }

        const newRecord = {
            id: 0,
            type: "setting",
            attributes: {
                settingIdToCopy: widget.settingIdToCopy
            },
            relationships: {
                profileId: DASHBOARD_PROFILE_ID,
                userLoginId: widget.userLoginId,
                settingData: settingData
            }
        };

        const url = '';
        let response: Response;

        response = await apiConnector.sendData(url, true, newRecord, true)

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === OK || response.status === CREATED) {

                    try {
                        const content = await response.json();
                        return resolve({ id: content?.attributes?.['id'], success: true })
                    } catch (error) {
                        return reject({ status: response.status, id: widget.id, message: genericError, success: false });
                    }
                }
            }

            return reject({ status: INTERNAL_SERVER_ERROR, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getWidgets = async (translation: any, userLoginId: string, permissions: string[], listQuery?: ListQuery): Promise<Widget[]> => {
    try {
        const filter: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const partialUrl = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filter);
        const fieldsSetting = "fields[setting]=sEventSysname,settingDatas";
        const fieldsSettingDatas = "fields[settingData]=sdValstring,element,site,location,zone,deviceImprox";
        const fieldsElement = "fields[element]=eSysname";
        const fieldsSite = 'fields[site]=siteName';
        const fieldsLocation = 'fields[location]=locName';
        const fieldsZone = 'fields[zone]=zoneName';
        const fieldsDeviceImprox = 'fields[deviceImprox]=devName';
        const url = `setting?filter=profile.id==${DASHBOARD_PROFILE_ID};userLogin.id==${userLoginId}&include=settingDatas.element,settingType,settingDatas.deviceImprox,settingDatas.zone,settingDatas.site,settingDatas.location&${fieldsSetting}&${fieldsElement}&${fieldsSettingDatas}&${fieldsSite}&${fieldsLocation}&${fieldsZone}&${fieldsDeviceImprox}${partialUrl}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                const widgets: Widget[] = [];
                data.forEach((setting: any) => {
                    const widget: Widget = {
                        id: setting["id"],
                        type: setting["s-event-sysname"],
                        ...getWidgetsDimensionByType(setting["s-event-sysname"]),
                        ...processSettingData(setting["setting-datas"], translation),
                    }

                    if (canTheUserViewThisWidget(widget, permissions)) {
                        widgets.push(widget);
                    }
                });
    
                return resolve(widgets);
            });
        });

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteWidget = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `setting/${id}`;
        const setting = {
            data: {
                type: "setting",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, setting);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const processSettingData = (settingDatas: any, translation:any): any => {
    let x: number = 0, y: number = 0, titleBackgroundColor: string = "", titleForegroundColor: string = "", relationships: any[] = [];
    let foregroundColor: string = "";
    const settingDatasRefs: { eSysname: string, id: string }[] = [];

    settingDatas.forEach((settingData: any) => {
        if (settingData["element"]["e-sysname"] === X_POSITION) {
            x = Number(settingData["sd-valstring"]);
            settingDatasRefs.push({ eSysname: X_POSITION, id: settingData["id"] })
        }
        else if (settingData["element"]["e-sysname"] === Y_POSITION) {
            y = Number(settingData["sd-valstring"]);
            settingDatasRefs.push({ eSysname: Y_POSITION, id: settingData["id"] })
        }
        else if (settingData["element"]["e-sysname"] === TITLE_BKGCOLOR) {
            titleBackgroundColor = settingData["sd-valstring"];
            settingDatasRefs.push({ eSysname: TITLE_BKGCOLOR, id: settingData["id"] })
        }
        else if (settingData["element"]["e-sysname"] === TITLE_FGCOLOR) {
            titleForegroundColor = settingData["sd-valstring"];
            settingDatasRefs.push({ eSysname: TITLE_FGCOLOR, id: settingData["id"] })
        }
        else if (settingData["element"]["e-sysname"] === QUICK_ENROLL_BKGCOLOR) {
            foregroundColor = settingData["sd-valstring"];
            settingDatasRefs.push({ eSysname: QUICK_ENROLL_BKGCOLOR, id: settingData["id"] })
        }
        else if(settingData["element"]["e-sysname"] === SITE_COMMANDS && settingData["site"] !== null) {
            relationships.push(
                {
                    eSysnameLookup: SITE_COMMANDS,
                    siteId: settingData["sd-valstring"],
                    name: settingData["site"]['site-name']
                }
            )
            settingDatasRefs.push({ eSysname: SITE_COMMANDS, id: settingData["id"] });
        }
        else if (settingData["element"]["e-sysname"] === LOCATION_COMMANDS && settingData["door"] !== null) {
            relationships.push(
                {
                    eSysnameLookup: LOCATION_COMMANDS,
                    doorId: settingData["sd-valstring"],
                    name: settingData["location"] ? settingData["location"]['loc-name'] : translation.byKey(TranslationKeys.defaultDoorName)
                }
            );
            settingDatasRefs.push({ eSysname: LOCATION_COMMANDS, id: settingData["id"] });
        }
        else if (settingData["element"]["e-sysname"] === ZONE_COMMANDS && settingData["zone"] !== null) {
            relationships.push(
                {
                    eSysnameLookup: ZONE_COMMANDS,
                    zoneId: settingData["sd-valstring"],
                    name: settingData["zone"] ? settingData["zone"]['zone-name'] : translation.byKey(TranslationKeys.defaultZoneName)
                }
            );
            settingDatasRefs.push({ eSysname: ZONE_COMMANDS, id: settingData["id"] });
        }
        else if (settingData["element"]["e-sysname"] === CONTROLLER_COMMANDS && settingData["device-improx"] !== null) {
            relationships.push(
                {
                    eSysnameLookup: CONTROLLER_COMMANDS,
                    controllerId: settingData["sd-valstring"],
                    name: settingData["device-improx"]['dev-name']
                }
            );
            settingDatasRefs.push({ eSysname: CONTROLLER_COMMANDS, id: settingData["id"] });
        }
        else if (settingData["element"]["e-sysname"] === READER_COMMANDS && settingData["device-improx"] !== null) {
            relationships.push(
                {
                    eSysnameLookup: READER_COMMANDS,
                    readerId: settingData["sd-valstring"],
                    name: settingData["device-improx"]['dev-name']
                }
            );
            settingDatasRefs.push({ eSysname: READER_COMMANDS, id: settingData["id"] });
        }
        else if (settingData["element"]["e-sysname"] === UI_ELEMENT_SELECT_MENU_ITEM) {
            relationships.push(
                {
                    eSysnameLookup: UI_ELEMENT_SELECT_MENU_ITEM,
                    menuId: settingData["sd-valstring"]
                }
            );
            settingDatasRefs.push({ eSysname: UI_ELEMENT_SELECT_MENU_ITEM, id: settingData["id"] });
        }
    });

    return {
        x,
        y,
        titleBackgroundColor,
        titleForegroundColor,
        foregroundColor,
        relationships,
        settingDatasRefs
    };
}

const getWidgetsDimensionByType = (type: string): { width: number, height: number } => {
    const widget = WIDGET_DIMENSION.find((widget) => widget.sEventSysname === type);

    if (widget) {
        return { width: widget.width, height: widget.height };
    }

    return { width: 0, height: 0 };
}

const canTheUserViewThisWidget = (widget: Widget, permissions: string[]) => {
    if (widget.type === WIDGET_TYPES.COMMANDS_WIDGET && permissions.includes("add.hardware.element")) {
        return true;
    }
    else if (widget.type === WIDGET_TYPES.LIVE_TRANSACTIONS_WIDGET && permissions.includes("show.transactions.element")) {
        return true;
    }
    else if (widget.type === WIDGET_TYPES.QUICK_ENROLL_WIDGET && permissions.includes("add.enrolment.element")) {
        return true;
    }
    else if (widget.type === WIDGET_TYPES.SHORTCUT_WIDGET) {
        const shortcutMenu = widget.relationships.find(relationship => relationship['eSysnameLookup'] === UI_ELEMENT_SELECT_MENU_ITEM);
        
        if (shortcutMenu) {
            const menu = MENUS.find(menu => menu.id === shortcutMenu['menuId']);
            const menuPath: string[] | undefined = menu?.path.split('/');
            
            if (menuPath && menuPath.length > 0) {
                const route = menuPath[menuPath.length - 1];
                return permissions.includes(`show.${route}.element`);
            }
        }

        return false;
    }

    return false;
}

export const doesWidgetExist = async (userLoginId: string, type: string): Promise<boolean> => {
    try {
        const url = `setting?filter=profile.id==${DASHBOARD_PROFILE_ID};userLogin.id==${userLoginId};settingEventSysname==${type}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                return resolve(data.length > 0);
            });
        });

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}
